import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@cmp-src/primitives/Dialog';
import { WorkplaceRequestStatus } from '@gettactic/api';
import { Button } from '@v2/components/ui/button';
import { Label } from '@v2/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@v2/components/ui/select';
import { STATUS_OPTIONS } from '@webV2/components/workplace-request/status-select/types';
import { useState } from 'react';
import { Textarea } from '../../../../components/ui/textarea';
import { WorkplaceRequestElement } from '@gettactic/api';
import { RequestStatus } from '../workplace-requests-settings/types';
import { StatusDisplay } from './StatusDisplay';
import { StatusChangeSummary } from './StatusChangeSummary';

interface StatusSelectWithModalProps {
  request: WorkplaceRequestElement;
  onStatusChange: (
    requestId: string,
    newStatus: RequestStatus,
    comment?: string
  ) => void;
  className?: string;
}

export const StatusSelectWithModal = ({
  request,
  onStatusChange,
  className = 'h-8 border-0 bg-transparent text-xs justify-start space-x-1'
}: StatusSelectWithModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<RequestStatus | null>(
    null
  );
  const [comment, setComment] = useState('');

  const handleStatusSelect = (value: string) => {
    
    const statusMap: Record<string, RequestStatus> = {
      open: 'Open',
      pending: 'Pending',
      closed: 'Closed'
    };
    const newStatus = statusMap[value] as RequestStatus;
    const currentStatus = request.status.charAt(0).toUpperCase() + request.status.slice(1);
    if (newStatus === currentStatus) return;

    setPendingStatus(newStatus);
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingStatus) {
      onStatusChange(request.id, pendingStatus, comment.trim());
      setComment('');
      setPendingStatus(null);
      setIsModalOpen(false);
    }
  };

  const handleCancel = () => {
    setComment('');
    setPendingStatus(null);
    setIsModalOpen(false);
  };


  const getDefaultMessage = (currentStatus: string, newStatus: string) => {
    if (newStatus === 'Pending') {
      return "We're currently working on your request and will update you with progress soon.";
    }
    if (newStatus === 'Closed') {
      return 'Your issue has been resolved. Please let us know if you need any further assistance.';
    }
    if (newStatus === 'Open') {
      return 'Your ticket has been reopened and is being reviewed by our team.';
    }
    return '';
  };

  
  const currentStatusLower = request.status as WorkplaceRequestStatus;

  return (
    <>
      <Select value={currentStatusLower} onValueChange={handleStatusSelect}>
        <SelectTrigger className={`${className} [&_[data-radix-select-icon]]:hidden`}>
          <div className="flex items-center gap-1">
            <SelectValue>
              <StatusDisplay status={request.status} />
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent className="z-dropdown">
          {STATUS_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <StatusDisplay status={option.label} className="space-x-2" />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent
          className="z-dropdown max-w-md rounded-lg bg-white"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="font-semibold text-primary">
              Update Request Status
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Status Change Summary */}
            <div onClick={(e) => e.stopPropagation()}>
              <StatusChangeSummary
                requestTitle={request.title}
                currentStatus={request.status}
                newStatus={pendingStatus || ''}
              />
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <Label
                  htmlFor="comment"
                  className="font-medium text-gray-700 text-sm"
                >
                  Message to {request.submitter_user?.name} (Optional)
                </Label>
                <p className="mb-2 text-gray-500 text-xs">
                  This message will be included in the Slack notification sent
                  to the request submitter.
                </p>
                <Textarea
                  id="comment"
                  value={comment}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setComment(e.target.value)
                  }
                  placeholder={getDefaultMessage(
                    request.status,
                    pendingStatus || ''
                  )}
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="flex space-x-3 pt-2">
                <Button
                  type="button"
                  onClick={handleCancel}
                  variant="outline"
                  className="flex-1 border-gray-300 bg-transparent hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  Update Status
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
